import { useParams, Link } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { articles } from '@/mocks/articles';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://superbuysheet.cyou';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articles[slug] : undefined;

  if (!article) {
    return (
      <main className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-24 pb-16 md:pb-20">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4">Article Not Found</h1>
            <p className="text-foreground-600 mb-6">The article you are looking for does not exist yet.</p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
              Back To Homepage
            </Link>
          </div>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.metaDescription,
            url: siteUrl + '/article/' + article.slug,
            inLanguage: 'en',
            isPartOf: {
              '@type': 'WebSite',
              name: 'Superbuy Sheet',
              url: siteUrl + '/',
            },
            publisher: {
              '@type': 'Organization',
              name: 'Superbuy Sheet',
              url: siteUrl + '/',
            },
          }),
        }}
      />
      <Navbar />

      <article className="pt-24 pb-16 md:pb-20">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <nav className="mb-8">
            <ol className="flex flex-wrap items-center gap-2 text-xs md:text-sm text-foreground-500">
              <li><Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">Home</Link></li>
              <li><i className="ri-arrow-right-s-line"></i></li>
              <li className="text-foreground-800 font-medium">{article.title.split(':')[0] || article.title}</li>
            </ol>
          </nav>

          <header className="mb-10">
            <span className="inline-block px-3 py-1 bg-secondary-100 text-secondary-700 rounded-full text-xs font-medium mb-4">
              {article.badge}
            </span>
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 leading-tight mb-4">
              {article.title}
            </h1>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
              Published: July 2026 &bull; {article.readingTime} &bull; An independent educational resource
            </p>
          </header>

          <div className="w-full h-[350px] md:h-[480px] rounded-2xl overflow-hidden mb-10">
            <img
              src={article.imageUrl}
              alt={article.imageAlt}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="text-sm md:text-base text-foreground-700 leading-relaxed space-y-6">
            {article.sections.map((section, idx) => {
              if (section.type === 'heading') {
                return (
                  <h2 key={idx} className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4">
                    {section.content}
                  </h2>
                );
              }
              if (section.type === 'subheading') {
                return (
                  <h3 key={idx} className="font-heading text-lg md:text-xl font-semibold text-foreground-800 mt-8 mb-3">
                    {section.content}
                  </h3>
                );
              }
              if (section.type === 'paragraph' && section.content) {
                return (
                  <p key={idx} className="mb-4" dangerouslySetInnerHTML={{ __html: section.content }} />
                );
              }
              if (section.type === 'cta') {
                return (
                  <div key={idx} className="my-8 p-6 bg-primary-50 border border-primary-200/50 rounded-2xl text-center">
                    <p className="text-primary-800 font-medium mb-4">{section.content}</p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                      <Link to="/" className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                        Back To Homepage
                      </Link>
                      <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="px-6 py-2.5 bg-secondary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-secondary-600 transition-colors whitespace-nowrap cursor-pointer">
                        Visit Main Platform
                      </a>
                    </div>
                  </div>
                );
              }
              if (section.type === 'faq' && section.faqItems) {
                return (
                  <section key={idx} className="mt-8">
                    <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                      Frequently Asked Questions
                    </h2>
                    <div className="space-y-4">
                      {section.faqItems.map((faq, fi) => (
                        <div key={fi}>
                          <h3 className="font-semibold text-foreground-900 text-base md:text-lg mb-2">
                            {faq.question}
                          </h3>
                          <p className="text-foreground-600 leading-relaxed">
                            {faq.answer}
                          </p>
                        </div>
                      ))}
                    </div>
                  </section>
                );
              }
              return null;
            })}

            <section className="mt-10 pt-8 border-t border-background-200/60">
              <div className="bg-background-100 rounded-2xl p-6 md:p-8 text-center">
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-3">
                  Ready To Start Shopping Smarter?
                </h3>
                <p className="text-sm text-foreground-600 mb-6 max-w-md mx-auto">
                  Explore our organized category collections and discover products faster with structured Superbuy Sheet resources.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <Link
                    to="/"
                    className="w-full sm:w-auto px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer text-center"
                  >
                    Back To Homepage
                  </Link>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="w-full sm:w-auto px-6 py-3 bg-secondary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-secondary-600 transition-colors whitespace-nowrap cursor-pointer text-center"
                  >
                    Visit Main Platform <i className="ri-external-link-line ml-1"></i>
                  </a>
                </div>
              </div>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {Object.values(articles)
                  .filter((a) => a.slug !== article.slug)
                  .slice(0, 4)
                  .map((a) => (
                    <Link
                      key={a.slug}
                      to={`/article/${a.slug}`}
                      className="flex items-center gap-3 p-4 bg-background-50 border border-background-200/60 rounded-xl hover:border-primary-300 transition-all cursor-pointer"
                    >
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 flex-shrink-0">
                        <i className="ri-article-line"></i>
                      </div>
                      <div>
                        <span className="text-xs text-foreground-500">{a.badge}</span>
                        <p className="text-sm font-medium text-foreground-800 leading-tight line-clamp-2">
                          {a.title}
                        </p>
                      </div>
                    </Link>
                  ))}
              </div>
            </section>
          </div>
        </div>
      </article>

      <Footer />
    </main>
  );
}